import React from 'react';
import { Globe, ShieldCheck, Clock, RefreshCw } from 'lucide-react';

export interface DomainItem {
  id: string;
  hostname: string;
  isPrimary: boolean;
  txtVerificationToken: string;
  tlsStatus: 'pending' | 'ready' | 'error';
  dnsVerifiedAt: string | null;
}

export interface DomainVerificationPanelProps {
  domains: DomainItem[];
  onTriggerVerify: (domainId: string) => void;
  isVerifying?: boolean;
}

// DomainVerificationPanel displays DNS TXT verification challenges and On-Demand TLS status (ADR-004).
export function DomainVerificationPanel({
  domains,
  onTriggerVerify,
  isVerifying,
}: DomainVerificationPanelProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 border-b border-border pb-4">
        <Globe className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Custom Domains & On-Demand TLS</h2>
      </div>

      <div className="flex flex-col gap-4">
        {domains.map((dom) => {
          const isVerified = dom.tlsStatus === 'ready';
          return (
            <div
              key={dom.id}
              className="p-5 rounded-xl border border-border bg-card flex flex-col gap-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="font-semibold text-base text-foreground">{dom.hostname}</span>
                  {dom.isPrimary && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary">
                      Primary
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isVerified ? (
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-accent bg-accent/15 px-2.5 py-1 rounded-full">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      <span>TLS Active</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
                      <Clock className="h-3.5 w-3.5" />
                      <span>DNS Pending</span>
                    </span>
                  )}
                  {!isVerified && (
                    <button
                      type="button"
                      onClick={() => onTriggerVerify(dom.id)}
                      disabled={isVerifying}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-50 min-h-[44px]"
                    >
                      <RefreshCw className={`h-3 w-3 ${isVerifying ? 'animate-spin' : ''}`} />
                      <span>Verify Now</span>
                    </button>
                  )}
                </div>
              </div>

              {!isVerified && (
                <div className="p-3 rounded-lg bg-muted/40 border border-border text-xs flex flex-col gap-1.5">
                  <span className="font-bold text-muted-foreground uppercase tracking-wider">
                    Required DNS TXT Record
                  </span>
                  <div className="flex flex-col md:flex-row md:items-center justify-between font-mono bg-background p-2 rounded border border-border">
                    <span className="text-muted-foreground">_helios-verify.{dom.hostname}</span>
                    <span className="text-foreground">{dom.txtVerificationToken}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
